import { supabase } from '../lib/supabase';
import { isValidRole, DEFAULT_ROLE, VALID_ROLES } from '../constants/roles';

export const profileService = {
  // Get user profile with retry logic
  async getProfile(userId, retryCount = 0) {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 500; // 500ms between retries

    try {
      console.log(`🔍 Fetching profile for user ${userId} (attempt ${retryCount + 1})`);
      
      // Verify we have an active session first
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        console.warn('⚠️ No active session found when fetching profile');
        throw new Error('No active session');
      }
      
      // Now make the query with verified session
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.log('🔍 Profile query error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });

        // If the profile doesn't exist, return null instead of throwing
        if (error.code === 'PGRST116' || error.message?.includes('No rows returned')) {
          console.log(`📝 Profile not found for user ${userId}`);
          return null;
        }

        // For RLS or connection errors, retry if we haven't exceeded max retries
        if (retryCount < MAX_RETRIES && (
          error.code === 'PGRST301' || 
          error.message?.includes('connection') ||
          error.message?.includes('timeout') ||
          error.message?.includes('RLS') ||
          error.code === '42501' // Permission denied
        )) {
          console.log(`🔄 Retrying profile fetch in ${RETRY_DELAY}ms... (Error: ${error.message})`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          return this.getProfile(userId, retryCount + 1);
        }

        throw error;
      }

      console.log(`✅ Profile found for user ${userId}:`, { role: data.role_type, email: data.email });
      return data;
    } catch (error) {
      console.error('❌ Get profile error:', error);
      
      // If this was our last retry, throw the error
      if (retryCount >= MAX_RETRIES) {
        throw error;
      }
      
      // Otherwise retry
      console.log(`🔄 Retrying profile fetch in ${RETRY_DELAY}ms...`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return this.getProfile(userId, retryCount + 1);
    }
  },

  // Update user profile
  async updateProfile(userId, updates) {
    try {
      // Validate role_type if it's being updated
      if (updates.role_type && !isValidRole(updates.role_type)) {
        throw new Error(`Invalid role_type: ${updates.role_type}. Must be one of: ${VALID_ROLES.join(', ')}.`);
      }

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  },

  // Create or update profile
  async upsertProfile(profile) {
    try {
      // Validate role_type
      if (profile.role_type && !isValidRole(profile.role_type)) {
        console.warn(`Invalid role_type: ${profile.role_type}. Defaulting to '${DEFAULT_ROLE}'.`);
        profile.role_type = DEFAULT_ROLE;
      }
      
      // Set default role_type if not provided
      if (!profile.role_type) {
        profile.role_type = DEFAULT_ROLE;
      }

      const { data, error } = await supabase
        .from('profiles')
        .upsert(profile)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Upsert profile error:', error);
      throw error;
    }
  },

  // Get all profiles (for admin or public viewing)
  async getProfiles(limit = 50, offset = 0) {
    try {
      // Check if current user is admin
      const { data: { session } } = await supabase.auth.getSession();
      const isAdmin = session?.user ? await this._isAdmin(session.user.id) : false;

      // Admins query full profiles table, others use public_profiles view
      const table = isAdmin ? 'profiles' : 'public_profiles';
      const columns = isAdmin ? '*' : '*'; // View already limits columns

      const { data, error, count } = await supabase
        .from(table)
        .select(columns, { count: 'exact' })
        .range(offset, offset + limit - 1)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { profiles: data, count };
    } catch (error) {
      console.error('Get profiles error:', error);
      throw error;
    }
  },

  // Helper: Check if user is admin (internal use)
  async _isAdmin(userId) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role_type')
        .eq('id', userId)
        .single();
      
      return !error && data?.role_type === 'admin';
    } catch {
      return false;
    }
  },

  // Check if email is verified
  async checkEmailVerification(userId) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('email_verified')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data.email_verified;
    } catch (error) {
      console.error('Check email verification error:', error);
      throw error;
    }
  }
};